import {
    associateWebAuthnCredential, listWebAuthnCredentials, deleteWebAuthnCredential
} from "aws-amplify/auth";
import useEnv from "./useEnv";
import { useState } from "react";

const usePasskeys = () => {
    const { isDev, isEnvTruthy, getEnv } = useEnv();
    const passkeysEnabledDefault = isEnvTruthy('USE_PASSKEYS') && localStorage.getItem('disablePasskeys') !== 'true';
    const [passkeysEnabled, setPasskeysEnabled] = useState(passkeysEnabledDefault);

    /**
     * Get list of trusted devices (WebAuthn credentials)
     * @returns {Promise<{
     *   credentialId: string|undefined,
     *   friendlyCredentialName: string|undefined,
     *   relyingPartyId: string|undefined,
     *   createdAt: string|undefined
     * }[]>}
     */
    const getPasskeys = async () => {
        try {
            const result = await listWebAuthnCredentials();
            return result?.credentials ?? [];
        } catch (err) {
            if (isDev) {
                console.error('Error listing passkey:', err);
            }
            return [];
        }
    }

    /**
     * Register a new passkey (WebAuthn credential) for the user and domain
     * @returns {Promise<void>}
     */
    const registerPasskey = async () => {
        try {
            await associateWebAuthnCredential();
        } catch (err) {
            // If registration fails or user cancels, disable passkeys to prevent repeated failures
            localStorage.setItem('disablePasskeys', 'true');
            if (isDev) {
                console.error('Error registering passkey:', err);
            }
        }
    }

    /**
     * Delete a passkey (WebAuthn credential) by its credentialId
     * @param {string} credentialId
     * @returns {Promise<void>}
     */
    const deletePasskey = async (credentialId) => {
        try {
            await deleteWebAuthnCredential({ credentialId });
        } catch (err) {
            if (isDev) {
                console.error('Error registering passkey:', err);
            }
        }
    }

    /**
     * Toggle passkeys enabled state and store preference in localStorage
     * @returns {Promise<void>}
     */
    const togglePasskeysEnabled = async () => {
        const newValue = !passkeysEnabled;
        setPasskeysEnabled(newValue);
        if (newValue) {
            localStorage.removeItem('disablePasskeys');
        } else {
            localStorage.setItem('disablePasskeys', 'true');
        }
    }

    /**
     * Get related origins from WEBAUTHN_CONFIG environment variable
     * @returns {string[]}
     */
    const getRelatedOrigins = () => {
        let config = getEnv('WEBAUTHN_CONFIG');
        if (config === undefined) {
            return [];
        }
        try {
            config = JSON.parse(config);
        } catch (e) {
            if (isDev) {
                console.error("WEBAUTHN_CONFIG is not valid JSON:", e);
            }
            config = undefined;
        }
        return config?.origins ?? [];
    }

    return ({
        getPasskeys,
        registerPasskey,
        deletePasskey,
        passkeysEnabled,
        togglePasskeysEnabled,
        getRelatedOrigins
    });
}

export default usePasskeys;
